import React from 'react';

import { POLICY_STATUS, SKELETON_ROWS } from '../constants';
import PolicyDetails from './PolicyDetails';
import ComplaintTypeDropdowns from './ComplaintTypeDropdowns';
import Button from '../../../../../components/ComponentsV1/Button';
import { Skeleton } from '../../../../../components/ComponentsV1/Skeleton/Skeleton';

const PolicySkeleton = () => (
  <div className='tw-bg-white tw-p-8 tw-rounded-2xl'>
    <Skeleton rows={SKELETON_ROWS} />
  </div>
);

const PolicyResult = ({
  policyStatus,
  policyDetails,
  isSubmitting,
  isUpdateMode,
  setIsUpdateMode,
  showDeleteConfirm,
  setShowDeleteConfirm,
  includedCTypes = [],
  setIncludedCTypes,
  excludedCTypes = [],
  setExcludedCTypes,
  complaintTypeOptions = [],
  isActionLoading,
  onUpdate,
  onDelete,
  onRestore,
  onCreate,
}) => {
  const handleUpdateClick = () => {
    setIsUpdateMode(true);
    const existingIncluded = (policyDetails?.included_complaint_types || [])
      .map((name) => complaintTypeOptions.find((opt) => opt.name === name))
      .filter(Boolean);
    const existingExcluded = (policyDetails?.excluded_complaint_types || [])
      .map((name) => complaintTypeOptions.find((opt) => opt.name === name))
      .filter(Boolean);
    setIncludedCTypes(existingIncluded);
    setExcludedCTypes(existingExcluded);
  };

  if (policyStatus === POLICY_STATUS.NONE) {
    if (isSubmitting) {
      return <PolicySkeleton />;
    }
    return null;
  }

  if (isActionLoading) {
    return <PolicySkeleton />;
  }

  // CASE: Policy exists and is active
  if (policyStatus === POLICY_STATUS.EXISTS) {
    return (
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-flex tw-flex-col tw-gap-6'>
        <div className='tw-flex tw-items-center tw-gap-2 tw-text-[1.6rem] tw-font-medium tw-text-green-700'>
        Policy exists
        </div>

        <PolicyDetails policyDetails={policyDetails} />

        {/* Update / Delete buttons */}
        {!isUpdateMode && !showDeleteConfirm && (
          <div className='tw-flex tw-flex-col tw-gap-3'>
            <div className='tw-text-[1.2rem] tw-text-zinc-500 tw-font-medium'>
              Update will modify existing complaint types and affect future complaint processing.
              <br />
              Delete will permanently remove this policy from the system.
              <br />
              Choose your action carefully as these changes impact complaint handling.
            </div>
            <div className='tw-flex tw-gap-4'>
              <Button
                title='Update'
                onClick={handleUpdateClick}
              />
              <Button
                title='Delete'
                type='outline'
                onClick={() => setShowDeleteConfirm(true)}
              />
            </div>
          </div>
        )}

        {/* Update form */}
        {isUpdateMode && (
          <div className='tw-flex tw-flex-col tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-lg tw-p-6'>
            <ComplaintTypeDropdowns
              includedCTypes={includedCTypes}
              setIncludedCTypes={setIncludedCTypes}
              excludedCTypes={excludedCTypes}
              setExcludedCTypes={setExcludedCTypes}
              complaintTypeOptions={complaintTypeOptions}
            />
            <div className='tw-flex tw-gap-4 tw-mt-6 tw-justify-center'>
              <Button
                title='Save Changes'
                onClick={onUpdate}
              />
              <Button
                title='Cancel'
                type='outline'
                onClick={() => {
                  setIsUpdateMode(false);
                  setIncludedCTypes([]);
                  setExcludedCTypes([]);
                }}
              />
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className='tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-rounded-lg tw-p-6' style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <div className='tw-text-[1.4rem] tw-font-medium tw-text-zinc-800'>
              Are you sure you want to delete this policy?
            </div>
            <div className='tw-flex tw-gap-4'>
              <Button
                title='Cancel'
                type='outline'
                onClick={() => setShowDeleteConfirm(false)}
              />
              <Button
                title='Confirm Delete'
                onClick={onDelete}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // CASE: Policy exists but is deleted
  if (policyStatus === POLICY_STATUS.DELETED) {
    return (
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-flex tw-flex-col tw-gap-6'>
        <div className='tw-flex tw-items-center tw-gap-2 tw-text-[1.6rem] tw-font-medium tw-text-zinc-500'>
          Policy has been deleted
        </div>

        <PolicyDetails policyDetails={policyDetails} />

        <div className='tw-mt-2'>
          <Button
            title='Restore Policy'
            onClick={onRestore}
          />
        </div>
      </div>
    );
  }

  // CASE: Policy does not exist
  if (policyStatus === POLICY_STATUS.NOT_FOUND) {
    return (
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-flex tw-flex-col tw-gap-6'>
        <div className='tw-flex tw-items-center tw-gap-2 tw-text-[1.6rem] tw-font-medium tw-text-zinc-700'>
          Policy does not exist. You can create a new one.
        </div>

        <ComplaintTypeDropdowns
          includedCTypes={includedCTypes}
          setIncludedCTypes={setIncludedCTypes}
          excludedCTypes={excludedCTypes}
          setExcludedCTypes={setExcludedCTypes}
          complaintTypeOptions={complaintTypeOptions}
        />

        <div className='tw-mt-2'>
          <Button
            title='Create Policy'
            onClick={onCreate}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default PolicyResult;
